import { useNavigate } from '@tanstack/react-router';
import { Button, CodeInput } from '@yembo/yemblocks-core';
import { toInternationalPhoneNumber } from '@yembo/yemblocks-core/dist/_util';
import { useCallback, useEffect, useState } from 'react';
import { MfaEmployee, putVerifyMfa } from 'src/queries/Login';
import { useSubmitOnEnterKey } from 'src/react/_hooks/useEnterKeySubmit';
import { LoginFormData } from '..';
import { loginDevice, notifyLockedAccount } from '../_util/common';
import { getVerifyMfaCaption } from '../_util/mfaChannels';
import { SectionHeader } from '../SectionHeader';

const MFA_CODE_LENGTH = 6;

interface Props {
  formData: LoginFormData;
}

export const VerifyMfaForm = (props: Props): React.JSX.Element | null => {
  const { mfaEmployee } = props.formData;

  useEffect(() => {
    if (!mfaEmployee) {
      window.location.assign('/');
    }
  }, [mfaEmployee]);

  if (!mfaEmployee) return null;

  return <VerifyMfaFormInternal {...props} mfaEmployee={mfaEmployee} />;
};

const VerifyMfaFormInternal = ({ formData, mfaEmployee }: Props & { mfaEmployee: MfaEmployee }): React.JSX.Element => {
  const { phone, email, preferredMfaChannel, isMfaCodeSent } = mfaEmployee;

  const [mfaCode, setMfaCode] = useState<string[]>([]);
  const [mfaCodeError, setMfaCodeError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const formattedPhone = phone ? toInternationalPhoneNumber(phone) : '';

  const mfaChannel = formData.mfaChannel || preferredMfaChannel;
  const isAuthenticatorApp = mfaChannel === 'totp';
  const caption = getVerifyMfaCaption({
    channel: mfaChannel,
    destination: mfaChannel === 'email' ? email : formattedPhone,
    // A channel picked on the resend screen was sent on the way here.
    isMfaCodeSent: isMfaCodeSent || !!formData.mfaChannel,
  });

  const handleMfaCodeChange = (code: string[]) => {
    setMfaCode(code);
    setMfaCodeError(undefined);
  };

  const onLogin = useCallback(async () => {
    const mfaData = {
      email: formData.email ?? '',
      password: formData.password ?? '',
      mfaCode: mfaCode?.join('') ?? '',
    };

    setIsLoading(true);

    try {
      const response = await putVerifyMfa(mfaData);
      loginDevice(response);
    } catch (error) {
      if (!notifyLockedAccount(error)) setMfaCodeError('Incorrect code');
    } finally {
      setIsLoading(false);
    }
  }, [formData.email, formData.password, mfaCode]);

  useSubmitOnEnterKey({ onSubmit: onLogin });

  return (
    <div className='verify-mfa-form authentication-form'>
      <SectionHeader heading='Verify account' caption={caption} />
      <CodeInput value={mfaCode} onChange={handleMfaCodeChange} length={MFA_CODE_LENGTH} errorMessage={mfaCodeError} />
      <span className='resend-otc-text'>
        {isAuthenticatorApp ? "Can't use your authenticator app?" : "Didn't get the code?"}
        <Button
          kind='flat'
          text={isAuthenticatorApp ? 'Get a code another way' : 'Resend code'}
          size='medium'
          className='resend-otc-button'
          // biome-ignore lint/suspicious/noExplicitAny: router constraint bypass
          onClick={() => navigate({ to: '/resend-mfa', search: true } as any)}
        />
      </span>
      <div className='buttons-wrapper'>
        <Button
          text='Login'
          onClick={onLogin}
          isDisabled={mfaCode?.join('').length !== MFA_CODE_LENGTH}
          isLoading={isLoading}
        />
        <Button
          text='Use backup code'
          kind='outline'
          // biome-ignore lint/suspicious/noExplicitAny: router constraint bypass
          onClick={() => navigate({ to: '/verify-backup-code', search: true } as any)}
          isDisabled={isLoading}
        />
      </div>
    </div>
  );
};
